
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useApplication } from './ApplicationContext';
import { MenuItem, getIcon, resolveIcon } from '../config/menuConfig';
import { getRouteByPath } from '../config/routes';
import blobticService from '../services/blobticService';
import { blobticMessage } from '../components/BlobticMessage';
import { useLanguage } from '../contexts/LanguageContext';

interface MenuContextType {
    menuItems: MenuItem[];
    isLoading: boolean;
    refreshMenu: () => void;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const useMenu = () => {
    const context = useContext(MenuContext);
    if (!context) {
        throw new Error('useMenu must be used within a MenuProvider');
    }
    return context;
};

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const { currentApp } = useApplication();
    
    const { t } = useLanguage();

    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Helper to recursively map string icon names to ReactNode
    const hydrateIcons = useCallback((items: any[]): MenuItem[] => {
        return items.map(item => {
            const newItem: MenuItem = { ...item };

            // 1. Try to resolve icon via central config (DB string, or Default Mapping)
            newItem.icon = resolveIcon(newItem);

            // 2. Fallback: If still no icon (e.g. not in default map), try route config
            if (!newItem.icon && newItem.href && newItem.href.startsWith('/')) {
                const routeConfig = getRouteByPath(newItem.href);
                if (routeConfig && routeConfig.icon) {
                    newItem.icon = routeConfig.icon;
                }
            }

            // Recurse for children
            if (item.child && item.child.length > 0) {
                newItem.child = hydrateIcons(item.child);
            }

            return newItem;
        });
    }, []);

    const loadMenu = useCallback(async () => {
        if (!isAuthenticated || !currentApp) {
            setMenuItems([]);
            return;
        }

        setIsLoading(true);
        try {
            console.log(`Loading menu for app: ${currentApp}`);

            const response = await blobticService.executeZoomRequest<any>({
                ZoomKey: 'config_menu',
                Action: {
                    OKey: 'Blobtic_Sys_Profil,config_menu,get_profil_menu', // Use Profil DLL
                }
            });

            if (response.success && response.data) {
                let menuData = response.data;

                // Handle array response (Zoom request usually returns array of rows)
                // Check if the array contains the result object (has menuJson or Menu) rather than being the menu itself
                if (Array.isArray(menuData) && menuData.length > 0) {
                    const firstItem = menuData[0];
                    if (firstItem && (typeof firstItem === 'object') && ('menuJson' in firstItem || 'Menu' in firstItem)) {
                        menuData = firstItem;
                    }
                }

                // Backend might return { Menu: "json_string" } or directly the object depending on serialization
                if (menuData.Menu && typeof menuData.Menu === 'string') {
                    try {
                        const parsed = JSON.parse(menuData.Menu);
                        setMenuItems(hydrateIcons(parsed));
                        return;
                    } catch (e) {
                        console.error("Failed to parse menu JSON", e);
                    }
                }
                // Handle new response format with menuJson property
                if (menuData.menuJson && typeof menuData.menuJson === 'string') {
                    try {
                        const parsed = JSON.parse(menuData.menuJson);
                        setMenuItems(hydrateIcons(parsed));
                        return;
                    } catch (e) {
                        console.error("Failed to parse menu JSON from menuJson", e);
                    }
                }

                // Handle if data is already object (unlikely given backend code but good for safety)
                if (Array.isArray(menuData)) {
                    setMenuItems(hydrateIcons(menuData));
                    return;
                }
            } else {
                console.warn(`No menu found for ${currentApp}, or API error`, response);

                // Show error if specific error code exists
                if (response.code) {
                    blobticMessage.error(t('common.messages.error'), t(response.code));
                } else if (!response.success && response.message) {
                    blobticMessage.error(t('common.messages.error'), response.message);
                }
            }

            setMenuItems([]);
        } catch (error) {
            console.error("Failed to load menu from database", error);
            setMenuItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, currentApp, hydrateIcons, blobticMessage.error, t]);

    useEffect(() => {
        loadMenu();
    }, [loadMenu]);

    return (
        <MenuContext.Provider value={{ menuItems, isLoading, refreshMenu: loadMenu }}>
            {children}
        </MenuContext.Provider>
    );
};

